import { useEffect, useRef, useState } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * DriftAlertToast — V23.15.23 (Enhancement Doctrine 49 « alerte de décalage »).
 *
 * Polling léger toutes les 60s sur `/intelligence/v2/sessions/{id}/drift-check`.
 * Si des modifications de workspace sont détectées DEPUIS le début de la
 * conversation actuelle (et que la session a > 1 message user), affiche un
 * banner ambré au-dessus du chat avec :
 *  - Liste des entités modifiées (max 3 visibles, +N pour le reste)
 *  - Bouton « Recalculer ma recommandation » qui injecte automatiquement
 *    un message utilisateur de relance contextualisée
 *
 * UX volontairement non-intrusif :
 *  - Si l'utilisateur ferme le toast, on ne le réaffiche pas pour les MÊMES
 *    events (mémorisé via lastSeenTs)
 *  - Le toast réapparaît si de NOUVEAUX events drift se produisent
 */
export default function DriftAlertToast({ sessionId, onAskRecompute }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const lastSeenTsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;

    const tick = async () => {
      try {
        const r = await api.get(`/workspace/intelligence/v2/sessions/${sessionId}/drift-check`);
        if (!mounted) return;
        const d = r.data || {};
        setData(d);
        // Si de nouveaux events sont arrivés depuis le dernier dismiss, on
        // ré-affiche (le toast revient même si fermé précédemment).
        const newestTs = (d.events_since || []).reduce(
          (acc, ev) => ((ev.ts || "") > acc ? ev.ts : acc),
          ""
        );
        if (newestTs && newestTs !== lastSeenTsRef.current && dismissed) {
          setDismissed(false);
        }
        lastSeenTsRef.current = newestTs;
      } catch {
        // Endpoint indispo (sync OFF, etc.) → silencieux
      }
    };
    tick();
    pollRef.current = setInterval(tick, 60000);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [sessionId, dismissed]);

  if (!data || !data.drift_detected || dismissed) return null;
  const events = data.events_since || [];
  if (events.length === 0) return null;

  const top3 = events.slice(0, 3);
  const overflow = Math.max(0, events.length - top3.length);

  const handleRecompute = () => {
    const labels = top3.map((e) => `${e.label || e.entity_id}`).join(", ");
    const prompt = `Plusieurs éléments ont évolué depuis le début de notre échange (${labels}${
      overflow ? ` et ${overflow} autre(s)` : ""
    }). Peux-tu réévaluer ta dernière recommandation à la lumière de ces changements ?`;
    if (onAskRecompute) onAskRecompute(prompt);
    setDismissed(true);
  };

  return (
    <div
      className="mx-5 mt-3 mb-1 rounded-lg border border-[#EF9F27]/40 bg-[#EF9F27]/[0.08] px-4 py-3 flex items-start gap-3"
      data-testid="drift-alert-toast"
      role="alert"
    >
      <AlertTriangle className="w-4 h-4 text-[#EF9F27] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-[#EF9F27]">
          Cette donnée a évolué depuis le début de notre échange
        </div>
        <div className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
          {top3.map((ev, idx) => (
            <span key={ev.entity_id || idx} className="inline-block">
              <span className="text-zinc-500">{ev.module}</span>
              {" · "}
              <span className="text-zinc-200">{ev.label || ev.entity_id || "—"}</span>
              {idx < top3.length - 1 && <span className="text-zinc-600"> · </span>}
            </span>
          ))}
          {overflow > 0 && (
            <span className="text-zinc-500"> +{overflow} autre(s)</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleRecompute}
          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#EF9F27]/15 hover:bg-[#EF9F27]/25 text-[#EF9F27] text-[11px] font-medium transition-colors"
          data-testid="drift-alert-recompute-btn"
        >
          <RefreshCw className="w-3 h-3" />
          Recalculer ma recommandation
        </button>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-zinc-500 hover:text-zinc-300 transition-colors flex-shrink-0"
        aria-label="Fermer l'alerte"
        data-testid="drift-alert-dismiss-btn"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
